import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, Snackbar, Alert, MenuItem
} from '@mui/material';
import { useEffect, useState } from 'react';
import axios from 'axios';
import urls from '../../Mobx/Urls';
import permissionProfileStore from '../../Mobx/PermissionProfileStore';

const AddAccessSerial = ({ open, onClose, onSuccess }) => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        date: { start: '', end: '' },
        maxUsers: '',
        permissionProfile: ''
    });

    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        permissionProfileStore.fetchProfiles({
            page: 1,
            limit: 1000,
            filters: {},
            sort: [{ field: 'name', order: 'asc' }]
        });
    }, []);

    const validateField = (key, value) => {
        const newErrors = { ...errors };

        if (key === 'name') {
            if (!value?.trim()) newErrors.name = 'שדה חובה';
            else delete newErrors.name;
        }

        if (key === 'email') {
            if (!value?.trim()) newErrors.email = 'שדה חובה';
            else if (!/\S+@\S+\.\S+/.test(value)) newErrors.email = 'אימייל לא חוקי';
            else delete newErrors.email;
        }

        if (key === 'maxUsers') {
            const number = parseInt(value, 10);
            if (!value?.trim()) newErrors.maxUsers = 'שדה חובה';
            else if (isNaN(number) || number <= 0) newErrors.maxUsers = 'יש להזין מספר חיובי';
            else delete newErrors.maxUsers;
        }

        if (key === 'start' || key === 'end') {
            if (!value) newErrors[key] = 'שדה חובה';
            else delete newErrors[key];
        }

        setErrors(newErrors);
    };

    const handleChange = (key, value) => {
        validateField(key, value);

        if (key === 'start' || key === 'end') {
            setForm(prev => ({ ...prev, date: { ...prev.date, [key]: value } }));
        } else {
            setForm(prev => ({ ...prev, [key]: value }));
        }
    };

    const handleSubmit = async () => {
        const validations = ['name', 'email', 'start', 'end', 'maxUsers'];
        const newErrors = {};

        validations.forEach(key => {
            const value = key === 'start' || key === 'end' ? form.date?.[key] : form[key];

            if (key === 'name') {
                if (!value?.trim()) newErrors.name = 'שדה חובה';
            }

            if (key === 'email') {
                if (!value?.trim()) newErrors.email = 'שדה חובה';
                else if (!/\S+@\S+\.\S+/.test(value)) newErrors.email = 'אימייל לא חוקי';
            }

            if (key === 'maxUsers') {
                const number = parseInt(value, 10);
                if (!value?.trim()) newErrors.maxUsers = 'שדה חובה';
                else if (isNaN(number) || number <= 0) newErrors.maxUsers = 'יש להזין מספר חיובי';
            }

            if (key === 'start' || key === 'end') {
                if (!value) newErrors[key] = 'שדה חובה';
            }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            showSnackbar('יש שדות לא חוקיים');
            return;
        }

        const maxUsersValue = parseInt(form.maxUsers, 10);
        if (isNaN(maxUsersValue) || maxUsersValue <= 0) {
            showSnackbar('מספר המשתמשים חייב להיות מספר חיובי');
            return;
        }

        const payload = { ...form };
        if (payload.permissionProfile === '') {
            payload.permissionProfile = null;
        }

        try {
            setIsSubmitting(true);
            await axios.post(urls.accessSerial.add, payload, { withCredentials: true });
            onSuccess();
            onClose();
            setForm({ name: '', email: '', date: { start: '', end: '' }, maxUsers: '', permissionProfile: '' });
            setErrors({});
        } catch (err) {
            showSnackbar(
                <>
                    שגיאה ביצירת סיריאל:<br />
                    {err?.toString()}<br />
                    {err?.response?.data}
                </>,
                'error'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>הוספת סיריאל חדש</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="איש קשר"
                                value={form.name}
                                onChange={e => handleChange('name', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.name)}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="אימייל"
                                type="email"
                                value={form.email}
                                onChange={e => handleChange('email', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.email)}
                                helperText={errors.email}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="תאריך התחלה"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={form.date.start}
                                onChange={e => handleChange('start', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.start)}
                                helperText={errors.start}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="תאריך סיום"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={form.date.end}
                                onChange={e => handleChange('end', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.end)}
                                helperText={errors.end}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="מספר משתמשים"
                                type="number"
                                value={form.maxUsers}
                                onChange={e => handleChange('maxUsers', e.target.value)}
                                fullWidth
                                required
                                inputProps={{ min: 1 }}
                                error={Boolean(errors.maxUsers)}
                                helperText={errors.maxUsers}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="פרופיל הרשאות"
                                select
                                value={form.permissionProfile || ''}
                                onChange={e => handleChange('permissionProfile', e.target.value)}
                                fullWidth
                                error={Boolean(errors.permissionProfile)}
                                helperText={errors.permissionProfile}
                            >
                                <MenuItem value="">ללא</MenuItem>
                                {permissionProfileStore.profiles.map(profile => (
                                    <MenuItem key={profile._id} value={profile._id}>
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isSubmitting}>ביטול</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>שמירה</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={null}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddAccessSerial;
